// frontend/src/utils/speciesUtils.js

/**
 * Clean fallback formatter for species IDs (e.g., 'pathogen_erysiphe_necator' -> 'Erysiphe Necator')
 */
export const formatFallbackName = (speciesId) => {
  if (!speciesId) return 'Unspecified';
  return String(speciesId)
    .replace(/^(crop_|pathogen_|pest_|disease_)/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Extracts string ID from string or species object
 */
export const getSpeciesId = (speciesInput) => {
  if (!speciesInput) return '';
  if (typeof speciesInput === 'string') return speciesInput;
  return speciesInput.id || speciesInput.speciesId || speciesInput.species_id || '';
};

/**
 * Returns the resolved common name for a species or hazard
 */
export const getSpeciesName = (speciesInput, t = null) => {
  if (!speciesInput) return 'Unspecified';

  const id = getSpeciesId(speciesInput);

  // 1. Try translation function if supplied
  if (t && id) {
    const hazardKey = `wizard.hazards.${id}`;
    const translatedHazard = t(hazardKey);
    if (translatedHazard && translatedHazard !== hazardKey) {
      return translatedHazard;
    }

    const cropKey = `wizard.crops.${id}`;
    const translatedCrop = t(cropKey);
    if (translatedCrop && translatedCrop !== cropKey) {
      return translatedCrop;
    }
  }

  // 2. Try explicit object property
  if (typeof speciesInput === 'object' && speciesInput !== null) {
    if (speciesInput.cultivar)
      return `${speciesInput.common_name} (${speciesInput.cultivar})`;
    if (speciesInput.hazard_cultivar)
      return `${speciesInput.hazard_common_name} (${speciesInput.hazard_cultivar})`;
    if (speciesInput.common_name) return speciesInput.common_name;
    if (speciesInput.hazard_common_name) return speciesInput.hazard_common_name;
  }

  // 3. Fallback to formatted string ID
  return formatFallbackName(id);
};

/**
 * Returns the resolved description for a species or hazard
 */
export const getSpeciesDescription = (speciesInput, t = null) => {
  if (!speciesInput) return '';

  const id = getSpeciesId(speciesInput);

  // 1. Try translation function if supplied
  if (t && id) {
    const descKey = `wizard.descriptions.${id}`;
    const translatedDesc = t(descKey);
    if (translatedDesc && translatedDesc !== descKey) {
      return translatedDesc;
    }
  }

  // 2. Try explicit object property
  if (typeof speciesInput === 'object' && speciesInput !== null) {
    if (speciesInput.hazard_description) return speciesInput.hazard_description;
    if (speciesInput.description) return speciesInput.description;
  }

  // 3. Default descriptive string
  const commonName = getSpeciesName(speciesInput, t);
  const category = (typeof speciesInput === 'object' && speciesInput?.category) || 'hazard';
  return `${commonName} (${category}) - Monitored for agronomic risk assessments and advisory alerts.`;
};


export const getSpeciesType = (speciesInput, t = null) => {
  if (!speciesInput) return '';

  const id = getSpeciesId(speciesInput);

  console.log("[getSpeciesType] speciesInput:", speciesInput);
  if (typeof speciesInput === 'object' && speciesInput !== null) {
    if (speciesInput.hazard_domain) return `${speciesInput.hazard_domain} `;
    if (speciesInput.common_domain) return `${speciesInput.common_domain}`;
  }

  return '--';
}



/**
 * Returns a standardized species object
 */
export const formatSpeciesInfo = (speciesInput, t = null) => {
  const id = getSpeciesId(speciesInput);
  const commonName = getSpeciesName(speciesInput, t);
  const description = getSpeciesDescription(speciesInput, t);

  const varietal = typeof speciesInput === 'object' ? (speciesInput.varietal || speciesInput.varietal_cultivar || null) : null;
  const category = typeof speciesInput === 'object' ? (speciesInput.category || speciesInput.common_category || null) : null;
  const domain = typeof speciesInput === 'object' ? (speciesInput.domain || speciesInput.common_domain || null) : null;

  return {
    id,
    commonName,
    description,
    varietal,
    category,
    domain
  };
};
